/**
 * AJAX add-to-cart for the main product form.
 *
 * A native POST to /cart/add reloads the page before the Rebuy drawer opens — measured at
 * 3-4s, worst in the Instagram in-app browser, where shoppers assumed the button had not
 * worked. This posts the item instead and opens the drawer in place.
 *
 * Deliberately narrow: it binds to the main form on the default and alternative product
 * sections and nothing else. Product cards render from a disabled section on every product
 * template, cross-sells belong to Rebuy, and the sticky bar has its own add-to-cart path.
 * A document-level listener would take on those cases without needing to.
 */
(function () {
	"use strict";

	// Scoped by section wrapper class, which Shopify renders from each section's schema
	// ("class": "main-product" / "main-product-alternative"). main-product-bundle declares no
	// class and its markup is otherwise identical — .mproduct, .mproduct__details and
	// .mproduct__form all the way down — so the wrapper is the only structural way to tell
	// them apart. That matters: a bundle submits several items and this single-item path
	// would silently add one and drop the rest.
	var MAIN_FORM_SELECTOR = [
		".main-product .mproduct__form.atc-form",
		".main-product-alternative .mproduct-alternative__form.atc-form"
	].join(", ");

	function readItem(form) {
		var data = new FormData(form);
		var id = data.get("id");
		var quantity = data.get("quantity");

		// No variant means we cannot build a valid request. Rather than guess, decline to
		// intercept and let the browser submit natively — the shopper still gets a cart.
		if (!id) return null;

		var item = { id: id, quantity: 1 };

		if (quantity !== null) {
			var parsed = Number(quantity);
			// A quantity the form produced but we cannot read is a broken contract, not a
			// reason to quietly add one of something.
			if (!Number.isInteger(parsed) || parsed < 1) return null;
			item.quantity = parsed;
		}

		var sellingPlan = data.get("selling_plan");
		if (sellingPlan) item.selling_plan = sellingPlan;

		var properties = {};
		var hasProperties = false;
		data.forEach(function (value, key) {
			var match = key.match(/^properties\[(.+)\]$/);
			if (!match) return;
			// Shopify drops blank line-item properties server-side; mirroring that keeps the
			// AJAX and native paths producing the same cart.
			if (value === "") return;
			properties[match[1]] = value;
			hasProperties = true;
		});
		if (hasProperties) item.properties = properties;

		return item;
	}

	// Only needed on the path where Rebuy is absent. When Rebuy is running it owns this
	// badge outright — it replaces the Liquid markup (a .cart-count wrapper around a
	// .cart-count__count span) with a bare .cart-count holding the number, and keeps it
	// current itself. Writing to it here would fight the app for no gain.
	function refreshCartCount() {
		var badge = document.querySelector(".cart-count");
		if (!badge) return Promise.resolve();

		return fetch("/cart.js", { credentials: "same-origin" })
			.then(function (response) {
				return response.ok ? response.json() : null;
			})
			.then(function (cart) {
				if (cart) badge.textContent = cart.item_count;
			})
			.catch(function () {
				// A stale badge is not worth surfacing; the item is already in the cart.
			});
	}

	function openCartDrawer() {
		var smartCart = window.Rebuy && window.Rebuy.SmartCart;
		if (smartCart && typeof smartCart.show === "function") {
			if (typeof smartCart.refresh === "function") smartCart.refresh();
			smartCart.show();
			return true;
		}
		return false;
	}

	function showError(form) {
		var message = form.querySelector("[data-atc-error]");
		if (!message) {
			message = document.createElement("p");
			message.className = "atc-error";
			message.setAttribute("data-atc-error", "");
			message.setAttribute("role", "alert");
			form.appendChild(message);
		}
		// The item may or may not have been added — a rejected request is not proof that
		// nothing happened. Never invite a blind second click.
		message.textContent = "We couldn't add this to your cart. Please check your cart before trying again.";
		message.hidden = false;
	}

	function clearError(form) {
		var message = form.querySelector("[data-atc-error]");
		if (message) message.hidden = true;
	}

	function addViaRebuy(item) {
		return Promise.resolve(window.Rebuy.Cart.addItem(item));
	}

	function addViaAjax(item) {
		return fetch("/cart/add.js", {
			method: "POST",
			headers: { "Content-Type": "application/json", Accept: "application/json" },
			body: JSON.stringify(item),
			credentials: "same-origin"
		}).then(function (response) {
			if (!response.ok) throw new Error("add-to-cart failed: " + response.status);
			return response.json();
		});
	}

	function handleSubmit(event) {
		var form = event.currentTarget;

		// Something else already decided this submit should not proceed — rtx-pdp blocks
		// required-subscription products whose plan configuration is broken. Honour that.
		if (event.defaultPrevented) return;

		var button = form.querySelector('button[type="submit"], input[type="submit"]');
		if (button && button.disabled) {
			event.preventDefault();
			return;
		}

		var item = readItem(form);
		if (!item) return; // fall through to a native submit

		event.preventDefault();
		clearError(form);
		if (button) button.disabled = true;

		// Pick the path before attempting anything. A failed attempt is never retried down
		// another path: we cannot tell whether the item landed, and adding again is how a
		// shopper ends up charged twice.
		var canUseRebuy = !!(window.Rebuy && window.Rebuy.Cart && typeof window.Rebuy.Cart.addItem === "function");
		var request = canUseRebuy ? addViaRebuy(item) : addViaAjax(item);

		request
			.then(function () {
				form.dataset.atcState = "added";
				return canUseRebuy ? null : refreshCartCount();
			})
			.then(function () {
				// Rebuy's own add opens the drawer; the plain Ajax path has to ask.
				if (!canUseRebuy && !openCartDrawer()) {
					// Nothing can present the cart, so send the shopper to it rather than
					// leaving them on a page that looks like nothing happened.
					window.location.href = "/cart";
				}
			})
			.catch(function (error) {
				form.dataset.atcState = "failed";
				console.error(error);
				showError(form);
			})
			.then(function () {
				if (button) button.disabled = false;
			});
	}

	function bind() {
		var forms = document.querySelectorAll(MAIN_FORM_SELECTOR);
		for (var i = 0; i < forms.length; i++) {
			if (forms[i].dataset.ajaxAtcBound === "true") continue;
			forms[i].dataset.ajaxAtcBound = "true";
			forms[i].addEventListener("submit", handleSubmit);
		}
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", bind, { once: true });
	} else {
		bind();
	}
})();
